import { isDefined } from "@clipboard-health/util-ts";
import { useMemo } from "react";

import { useGetPreShiftOrientations } from "./useGetPreShiftOrientations";

interface BookedShiftOrientation {
  hasPreShiftOrientation: boolean;
  adjustedStart?: Date;
  originalStart?: Date;
  orientationLengthInMinutes?: number;
  isLoading: boolean;
}

/**
 * Returns the pre-shift orientation state for a given booked shift by looking
 * up the authenticated worker's active `PreShiftOrientation` records.
 *
 * The backend stores the adjusted start on the shift itself and the pre-shift
 * orientation record holds the `originalStart`. When a record exists for this
 * shift, the current `shift.start` is the adjusted start.
 */
export function useBookedShiftOrientation(shift: {
  id: string;
  start: string | Date;
}): BookedShiftOrientation {
  const { data, isLoading } = useGetPreShiftOrientations();

  return useMemo(() => {
    const preShiftOrientation = (data?.data ?? []).find(
      (orientation) => orientation.relationships.shift.data.id === shift.id
    );

    if (!isDefined(preShiftOrientation)) {
      return { hasPreShiftOrientation: false, isLoading };
    }

    return {
      hasPreShiftOrientation: true,
      adjustedStart: new Date(shift.start),
      originalStart: new Date(preShiftOrientation.attributes.originalStart),
      orientationLengthInMinutes: preShiftOrientation.attributes.orientationLengthInMinutes,
      isLoading,
    };
  }, [data?.data, isLoading, shift.id, shift.start]);
}
